import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAdmin } from "../../App";
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  LogOut,
  ShieldCheck,
  ArrowLeft,
  Save,
  Plus,
  X,
  ImagePlus,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const AdminPropertyForm = () => {
  const { propertyId } = useParams();
  const isEdit = Boolean(propertyId);
  const { admin, adminLogout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    builder: "",
    city: "",
    locality: "",
    address: "",
    property_type: "Apartment",
    category: "new_launch",
    configurations: ["2 BHK"],
    price_min: "",
    price_max: "",
    carpet_area_min: "",
    carpet_area_max: "",
    total_units: "",
    available_units: "",
    possession_date: "",
    rera_id: "",
    rera_state: "",
    amenities: [],
    images: [""],
    floor_plans: [],
    description: "",
    highlights: [],
    pros: [],
    cons: [],
    latitude: "",
    longitude: "",
    rating: "4.0",
    reviews_count: "0",
    featured: false,
  });

  const [newAmenity, setNewAmenity] = useState("");
  const [newHighlight, setNewHighlight] = useState("");
  const [newPro, setNewPro] = useState("");
  const [newCon, setNewCon] = useState("");
  const [newConfig, setNewConfig] = useState("");

  useEffect(() => {
    if (isEdit) {
      fetchProperty();
    }
  }, [propertyId, adminToken]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/admin/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const prop = response.data;
      setFormData({
        name: prop.name || "",
        builder: prop.builder || "",
        city: prop.city || "",
        locality: prop.locality || "",
        address: prop.address || "",
        property_type: prop.property_type || "Apartment",
        category: prop.category || "new_launch",
        configurations: prop.configurations || ["2 BHK"],
        price_min: prop.price_min?.toString() || "",
        price_max: prop.price_max?.toString() || "",
        carpet_area_min: prop.carpet_area_min?.toString() || "",
        carpet_area_max: prop.carpet_area_max?.toString() || "",
        total_units: prop.total_units?.toString() || "",
        available_units: prop.available_units?.toString() || "",
        possession_date: prop.possession_date || "",
        rera_id: prop.rera_id || "",
        rera_state: prop.rera_state || "",
        amenities: prop.amenities || [],
        images: prop.images?.length > 0 ? prop.images : [""],
        floor_plans: prop.floor_plans || [],
        description: prop.description || "",
        highlights: prop.highlights || [],
        pros: prop.pros || [],
        cons: prop.cons || [],
        latitude: prop.latitude?.toString() || "",
        longitude: prop.longitude?.toString() || "",
        rating: prop.rating?.toString() || "4.0",
        reviews_count: prop.reviews_count?.toString() || "0",
        featured: prop.featured || false,
      });
    } catch (error) {
      toast.error("Failed to fetch property");
      navigate("/admin/properties");
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData((prev) => ({ ...prev, images: newImages }));
  };

  const addImage = () => {
    setFormData((prev) => ({ ...prev, images: [...prev.images, ""] }));
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, images: newImages.length > 0 ? newImages : [""] }));
  };

  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      setFormData((prev) => ({ ...prev, [field]: [...prev[field], value.trim()] }));
      setter("");
    }
  };

  const removeFromArray = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const required = ["name", "builder", "city", "locality", "address", "rera_id", "rera_state", "price_min", "price_max"];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`${field.replace("_", " ")} is required`);
        return false;
      }
    }
    return true;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const payload = {
        ...formData,
        price_min: parseFloat(formData.price_min),
        price_max: parseFloat(formData.price_max),
        carpet_area_min: parseFloat(formData.carpet_area_min) || 0,
        carpet_area_max: parseFloat(formData.carpet_area_max) || 0,
        total_units: parseInt(formData.total_units) || 0,
        available_units: parseInt(formData.available_units) || 0,
        latitude: parseFloat(formData.latitude) || 0,
        longitude: parseFloat(formData.longitude) || 0,
        rating: parseFloat(formData.rating) || 4.0,
        reviews_count: parseInt(formData.reviews_count) || 0,
        images: formData.images.filter((img) => img.trim()),
      };

      if (isEdit) {
        await axios.put(`${API}/admin/properties/${propertyId}`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        toast.success("Property updated successfully");
      } else {
        await axios.post(`${API}/admin/properties`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        toast.success("Property created successfully");
      }
      navigate("/admin/properties");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save property");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate("/admin");
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Building2, label: "Properties", path: "/admin/properties", active: true },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/inquiries" },
  ];

  const cities = ["Bengaluru", "Mumbai", "Hyderabad", "Chennai", "Pune", "Delhi-NCR"];
  const states = ["Karnataka", "Maharashtra", "Telangana", "Tamil Nadu", "Haryana", "Delhi"];
  const propertyTypes = ["Apartment", "Villa", "Plot", "Penthouse", "Studio"];
  const categories = [
    { value: "new_launch", label: "New Launch" },
    { value: "under_construction", label: "Under Construction" },
    { value: "ready_to_move", label: "Ready to Move" },
    { value: "upcoming", label: "Upcoming" },
  ];
  const defaultConfigs = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK"];

  if (fetching) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 flex" data-testid="admin-property-form">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-800 border-r border-stone-700 flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg text-white">RERA Admin</div>
              <div className="text-xs text-stone-400">Control Panel</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    item.active
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "text-stone-400 hover:bg-stone-700 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {admin?.name?.charAt(0) || "A"}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <p className="text-stone-400 text-xs">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-stone-800 border-b border-stone-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/properties" className="text-stone-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <div>
                <h1 className="font-display text-2xl text-white">
                  {isEdit ? "Edit Property" : "Add New Property"}
                </h1>
                <p className="text-stone-400 text-sm">
                  {isEdit ? "Update property details" : "Create a new RERA-registered property"}
                </p>
              </div>
            </div>
            <Button
              onClick={handleSubmitClick}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="save-property-btn"
            >
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {loading ? "Saving..." : isEdit ? "Update Property" : "Save Property"}
            </Button>
          </div>
        </header>

        <div className="p-8">
          <form className="space-y-8">
            {/* Basic Info */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Project Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Prestige Lakeside Habitat"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    data-testid="property-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Builder Name *
                  </label>
                  <Input
                    value={formData.builder}
                    onChange={(e) => handleInputChange("builder", e.target.value)}
                    placeholder="e.g., Prestige Group"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    data-testid="property-builder-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Property Type
                  </label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(v) => handleInputChange("property_type", v)}
                  >
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Category
                  </label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => handleInputChange("category", v)}
                  >
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Enter project description..."
                    rows={4}
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange("featured", e.target.checked)}
                    className="w-4 h-4 rounded border-stone-600 bg-stone-700 text-amber-500"
                  />
                  <label htmlFor="featured" className="text-stone-300">
                    Mark as Featured Property
                  </label>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Location Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    City *
                  </label>
                  <Select
                    value={formData.city}
                    onValueChange={(v) => handleInputChange("city", v)}
                  >
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Locality *
                  </label>
                  <Input
                    value={formData.locality}
                    onChange={(e) => handleInputChange("locality", e.target.value)}
                    placeholder="e.g., Whitefield"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Full Address *
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    placeholder="Complete address with pincode"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Latitude
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange("latitude", e.target.value)}
                    placeholder="e.g., 12.9698"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Longitude
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange("longitude", e.target.value)}
                    placeholder="e.g., 77.7500"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
              </div>
            </div>

            {/* RERA Info */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">RERA Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    RERA Registration ID *
                  </label>
                  <Input
                    value={formData.rera_id}
                    onChange={(e) => handleInputChange("rera_id", e.target.value)}
                    placeholder="e.g., PRM/KA/RERA/1251/..."
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    data-testid="property-rera-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    RERA State *
                  </label>
                  <Select
                    value={formData.rera_state}
                    onValueChange={(v) => handleInputChange("rera_state", v)}
                  >
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Pricing & Units</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Minimum Price (₹) *
                  </label>
                  <Input
                    type="number"
                    value={formData.price_min}
                    onChange={(e) => handleInputChange("price_min", e.target.value)}
                    placeholder="e.g., 7500000"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Maximum Price (₹) *
                  </label>
                  <Input
                    type="number"
                    value={formData.price_max}
                    onChange={(e) => handleInputChange("price_max", e.target.value)}
                    placeholder="e.g., 15000000"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Possession Date
                  </label>
                  <Input
                    value={formData.possession_date}
                    onChange={(e) => handleInputChange("possession_date", e.target.value)}
                    placeholder="e.g., December 2026"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Total Units
                  </label>
                  <Input
                    type="number"
                    value={formData.total_units}
                    onChange={(e) => handleInputChange("total_units", e.target.value)}
                    placeholder="e.g., 1000"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Available Units
                  </label>
                  <Input
                    type="number"
                    value={formData.available_units}
                    onChange={(e) => handleInputChange("available_units", e.target.value)}
                    placeholder="e.g., 250"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
              </div>
            </div>

            {/* Area & Configurations */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Area & Configurations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Min Carpet Area (sq.ft)
                  </label>
                  <Input
                    type="number"
                    value={formData.carpet_area_min}
                    onChange={(e) => handleInputChange("carpet_area_min", e.target.value)}
                    placeholder="e.g., 950"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Max Carpet Area (sq.ft)
                  </label>
                  <Input
                    type="number"
                    value={formData.carpet_area_max}
                    onChange={(e) => handleInputChange("carpet_area_max", e.target.value)}
                    placeholder="e.g., 2500"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                  Configurations
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.configurations.map((config, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-stone-700 text-stone-300 text-sm rounded-sm flex items-center gap-2"
                    >
                      {config}
                      <button
                        type="button"
                        onClick={() => removeFromArray("configurations", i)}
                        className="text-stone-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" strokeWidth={2} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Select value={newConfig} onValueChange={setNewConfig}>
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white w-40">
                      <SelectValue placeholder="Add config" />
                    </SelectTrigger>
                    <SelectContent>
                      {defaultConfigs.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    onClick={() => addToArray("configurations", newConfig, setNewConfig)}
                    variant="outline"
                    className="border-stone-600 text-stone-400 hover:bg-stone-700"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Images</h2>
              <div className="space-y-3">
                {formData.images.map((img, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={img}
                      onChange={(e) => handleImageChange(i, e.target.value)}
                      placeholder="Image URL (https://...)"
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    />
                    <Button
                      type="button"
                      onClick={() => removeImage(i)}
                      variant="outline"
                      className="border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-red-400"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addImage}
                  variant="outline"
                  className="border-stone-600 text-stone-400 hover:bg-stone-700"
                >
                  <ImagePlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Add Image URL
                </Button>
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Amenities</h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.amenities.map((amenity, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-emerald-900/30 text-emerald-400 text-sm rounded-sm flex items-center gap-2"
                  >
                    {amenity}
                    <button
                      type="button"
                      onClick={() => removeFromArray("amenities", i)}
                      className="text-emerald-600 hover:text-red-400"
                    >
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("amenities", newAmenity, setNewAmenity))}
                  placeholder="Add amenity (e.g., Swimming Pool)"
                  className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                />
                <Button
                  type="button"
                  onClick={() => addToArray("amenities", newAmenity, setNewAmenity)}
                  variant="outline"
                  className="border-stone-600 text-stone-400 hover:bg-stone-700"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            {/* Highlights */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Highlights</h2>
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.highlights.map((highlight, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-amber-900/30 text-amber-400 text-sm rounded-sm flex items-center gap-2"
                  >
                    {highlight}
                    <button
                      type="button"
                      onClick={() => removeFromArray("highlights", i)}
                      className="text-amber-600 hover:text-red-400"
                    >
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newHighlight}
                  onChange={(e) => setNewHighlight(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("highlights", newHighlight, setNewHighlight))}
                  placeholder="Add highlight (e.g., Lake View)"
                  className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                />
                <Button
                  type="button"
                  onClick={() => addToArray("highlights", newHighlight, setNewHighlight)}
                  variant="outline"
                  className="border-stone-600 text-stone-400 hover:bg-stone-700"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Pros</h2>
                <div className="space-y-2 mb-4">
                  {formData.pros.map((pro, i) => (
                    <div key={i} className="flex items-center gap-2 text-green-400 text-sm">
                      <span className="text-green-500">+</span>
                      <span className="flex-1">{pro}</span>
                      <button
                        type="button"
                        onClick={() => removeFromArray("pros", i)}
                        className="text-stone-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newPro}
                    onChange={(e) => setNewPro(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("pros", newPro, setNewPro))}
                    placeholder="Add a pro"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                  <Button
                    type="button"
                    onClick={() => addToArray("pros", newPro, setNewPro)}
                    variant="outline"
                    className="border-stone-600 text-stone-400 hover:bg-stone-700"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>

              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Cons</h2>
                <div className="space-y-2 mb-4">
                  {formData.cons.map((con, i) => (
                    <div key={i} className="flex items-center gap-2 text-red-400 text-sm">
                      <span className="text-red-500">-</span>
                      <span className="flex-1">{con}</span>
                      <button
                        type="button"
                        onClick={() => removeFromArray("cons", i)}
                        className="text-stone-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newCon}
                    onChange={(e) => setNewCon(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addToArray("cons", newCon, setNewCon))}
                    placeholder="Add a con"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                  <Button
                    type="button"
                    onClick={() => addToArray("cons", newCon, setNewCon)}
                    variant="outline"
                    className="border-stone-600 text-stone-400 hover:bg-stone-700"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Rating */}
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
              <h2 className="font-display text-lg text-white mb-6">Rating & Reviews</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Rating (1-5)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={formData.rating}
                    onChange={(e) => handleInputChange("rating", e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Reviews Count
                  </label>
                  <Input
                    type="number"
                    value={formData.reviews_count}
                    onChange={(e) => handleInputChange("reviews_count", e.target.value)}
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button (Mobile) */}
            <div className="md:hidden">
              <Button
                type="button"
                onClick={handleSubmitClick}
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12"
              >
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {loading ? "Saving..." : isEdit ? "Update Property" : "Save Property"}
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-stone-800 border-stone-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {isEdit ? "Update Property" : "Create Property"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Are you sure you want to {isEdit ? "update" : "create"} this property?
              <br /><br />
              <strong className="text-white">{formData.name}</strong>
              <br />
              <span className="text-stone-500">by {formData.builder} in {formData.city}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-stone-700 text-white hover:bg-stone-600 border-stone-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedSubmit}
              className="bg-amber-500 text-white hover:bg-amber-600"
              data-testid="confirm-save-btn"
            >
              {isEdit ? "Update" : "Create"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPropertyForm;
